import api from "./api";
import {type Book} from "../../types/database";

import {useMutation, useQueryClient} from "react-query";

/**
 * Мутація для одиничної дії (save одного формату / togglePublish).
 * mutate() приймає callback, що повертає Promise<UpdateBookResponse>,
 * тож onSuccess отримує строго типізований res.id.
 *
 * Використання:
 *   const persistSingle = usePersistSingleMutation(id);
 *   persistSingle.mutate(
 *     () => updateBook(targetId, buildEbookUpdatePayload({ isPublic: on })),
 *     {
 *       onSuccess: (res) => setEbookId(res.id),
 *       onError: () => toast(...),
 *     },
 *   );
 */
export function usePersistSingleMutation(anchorId: string) {
    const queryClient = useQueryClient();

    return useMutation<UpdateBookResponse, unknown, () => Promise<UpdateBookResponse>>(
        (fn) => fn(),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["book", anchorId, "siblings"]);
            },
        },
    );

}

export interface SaveBothResult {
    ebookRes: UpdateBookResponse;
    audioRes: UpdateBookResponse;
}

/**
 * Мутація для handleSave — оновлює обидва формати послідовно й повертає
 * обидва результати разом, теж без unknown/каста.
 *
 * Використання:
 *   const persistBoth = usePersistBothMutation(id);
 *   persistBoth.mutate(
 *     async () => ({
 *       ebookRes: await updateBook(ebookId ?? id, buildEbookUpdatePayload()),
 *       audioRes: await updateBook(audiobookId ?? id, buildAudiobookUpdatePayload()),
 *     }),
 *     {
 *       onSuccess: ({ ebookRes, audioRes }) => {
 *         setEbookId(ebookRes.id);
 *         setAudiobookId(audioRes.id);
 *       },
 *       onError: () => toast(...),
 *     },
 *   );
 */
export function usePersistBothMutation(anchorId: string) {
    const queryClient = useQueryClient();

    return useMutation<SaveBothResult, unknown, () => Promise<SaveBothResult>>(
        (fn) => fn(),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(["book", anchorId, "siblings"]);
            },
        },
    );

}

interface RawTimestamp {
    _seconds?: number;
    _nanoseconds?: number;
}

type RawDate = RawTimestamp | string | number | null | undefined;

interface RawBook {
    id: string;
    name?: string;
    authors?: unknown[];
    cover?: string;
    coverFullSize?: string;
    licenseURL?: string | null;
    demoURL?: string | null;
    genres?: unknown[];
    priceId?: string;
    description?: string;
    descriptionHTML?: string;
    publishingHouse?: string;
    transliteratedName?: string;
    ISBN?: string;
    recommendedPrice?: string | null;
    publishingDate?: RawDate;
    isPublic?: boolean;
    showDemo?: boolean;
    type?: "ebook" | "audiobook";
    createdAt?: RawDate;
    rightsValidityTerm?: RawDate;
    audiobook?: Record<string, unknown> | null;
    lcpId?: string | null;
    chapters?: string[];
    relations?: { id: string; type: "ebook" | "audiobook" }[];
}


interface BookMetaDataBase {
    cover?: string;
    coverFullSize?: string;
    demoURL?: string | null;
    ISBN?: string;
    recommendedPrice?: string | null;
    publishingDate?: RawDate;
    rightsValidityTerm?: RawDate;
    isPublic: boolean;
    showDemo: boolean;
    lcpId?: string | null;
}

export interface EbookPayload extends BookMetaDataBase {
    type: 'ebook';
}

export interface AudiobookPayload extends BookMetaDataBase {
    type: 'audiobook';
    chapters?: string[];
}

export type BookVersionPayload = EbookPayload | AudiobookPayload;

export interface CreateBookPayload {
    name: string;
    authors: string[];
    description?: string;
    descriptionHTML?: string;
    publishingHouse?: string;
    genres: string[];
    transliteratedName?: string;
    licenseURL?: string | null;
    versions: BookVersionPayload[];
}

export interface CreateBookResponse {
    ids: string[];
}


function toIso(v: RawDate): string {
    if (!v) return "";
    if (typeof v === "object" && typeof v._seconds === "number") {
        return new Date(v._seconds * 1000).toISOString();
    }
    if (typeof v === "number") {
        // Algolia indexes createdAt as a number; tolerate seconds vs milliseconds.
        const ms = v < 1e12 ? v * 1000 : v;
        return new Date(ms).toISOString();
    }
    if (typeof v === "string") {
        const d = new Date(v);
        return Number.isNaN(d.getTime()) ? "" : d.toISOString();
    }
    return "";
}

export function mapBooks(rawBooks?: RawBook[]): Book[] {
    return (rawBooks ?? []).map(mapBook);
}

export function mapBook(raw: RawBook): Book {
    return {
        id: raw.id,
        name: raw.name ?? "",
        authors: Array.isArray(raw.authors) ? raw.authors.map((a) => String(a)) : [],
        cover: raw.cover ?? "",
        coverFullSize: raw.coverFullSize ?? "",
        licenseURL: raw.licenseURL ?? "",
        demoURL: raw.demoURL ?? "",
        genres: Array.isArray(raw.genres)
            ? raw.genres.map((g) => String(g)).filter(Boolean)
            : [],
        priceId: raw.priceId ?? "",
        description: raw.description ?? "",
        descriptionHTML: raw.descriptionHTML ?? "",
        publishingHouse: raw.publishingHouse ?? "",
        transliteratedName: raw.transliteratedName ?? "",
        isbn: raw.ISBN ?? "",
        recommendedPrice: raw.recommendedPrice ?? "",
        publishingDate: toIso(raw.publishingDate),
        isPublic: Boolean(raw.isPublic),
        showDemo: Boolean(raw.showDemo),
        type: raw.type,
        createdAt: toIso(raw.createdAt),
        rightsValidityTerm: toIso(raw.rightsValidityTerm),
        audioBookMap: raw.audiobook ?? null,
        lcpId: raw.lcpId ?? "",
        fileUrl: "",
        relations: raw.relations ?? [],
        chapters: raw.chapters ?? [],   // ← ДОДАТИ
    };
}

export interface BookWithSiblings {
    ebook?: Book;
    audiobook?: Book;
}


/** Читає книгу за id + (якщо є) її лінкований сиблінг іншого формату. */
export async function getBookWithSiblings(id: string): Promise<BookWithSiblings> {
    const primary = await getBook(id);
    const result: BookWithSiblings = {
        [primary.type ?? "ebook"]: primary,
    } as BookWithSiblings;

    const siblingRelation = (primary.relations ?? []).find((r) => r.type !== primary.type);
    if (siblingRelation) {
        const sibling = await getBook(siblingRelation.id);
        result[sibling.type ?? siblingRelation.type] = sibling;
    }
    return result;
}

interface BooksListResponse {
    books?: RawBook[];
    paging?: { currentPage: number; limit: number; totalElements: number };
}

/**
 * Books for a publishing house INCLUDING drafts. Source-of-truth method:
 * `booksByPublisher?includeUnpublished=true` — the gateway honors the flag only
 * for the authenticated owner-publisher of that house (or a superadmin); for an
 * anonymous caller it returns the public catalog only.
 */
export async function getBooksForPublisher(
    publisher: string,
    opts?: { searchParameter?: string },
): Promise<Book[]> {
    const params: Record<string, string | number | boolean> = {
        publisher,
        includeUnpublished: true,
        page: 1,
        limit: 99,
    };

    if (opts?.searchParameter?.trim()) {
        params.searchParameter = opts.searchParameter.trim();
    }

    const data = await api.get<unknown, BooksListResponse>(
        "/booksByPublisher",
        { params },
    );
    return mapBooks(data?.books);
}

export async function getSpecificBooks(
    userId: string,
    bookIds: string[],
): Promise<Book[]> {
    if (bookIds.length === 0) return [];

    const data = await api.post<unknown, BooksListResponse>("/getBooksFromList", {
        user_id: userId,
        books_id: bookIds,
    });

    return mapBooks(data?.books);
}

export async function deleteBook(id: string): Promise<void> {
    await api.delete("/deleteBook", {params: {id}});
}

/** Create a draft (backend owns the id) and return it. */
export async function createDraftBook(
    publishingHouse: string,
    type: "ebook" | "audiobook" = "ebook",
    name = "Без назви",
): Promise<string> {
    const ids = await createBook({
        name,
        authors: [],
        genres: [],
        publishingHouse,
        versions: [
            type === "audiobook"
                ? {type: "audiobook", isPublic: false, showDemo: false, chapters: []}
                : {type: "ebook", isPublic: false, showDemo: false},
        ],
    });
    return ids[0];
}

/** Read a single book (drafts included) for the edit screen. */
export async function getBook(id: string): Promise<Book> {
    const raw = await api.get<unknown, RawBook>("/getBook", {params: {id}});
    return mapBook(raw);
}

export async function uploadCover(id: string, file: File): Promise<string> {
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post<unknown, { cover: string }>("/updateBookCover", fd, {
        params: {id},
    });
    return res.cover;
}

/** Send the epub to the CMS/LCP pipeline; returns the issued lcpId (licence). */
export async function packageEbook(
    file: File,
    title: string,
): Promise<string> {
    const fd = new FormData();
    fd.append("file", file);

    const res = await api.post<unknown, string>(
        "/packageEbook",
        fd,
        {
            params: {title},
        }
    );

    return res;
}

/** Auto-generate the demo epub from the book file; returns the new demoURL. */
export async function generateBookDemo(id: string, file: File): Promise<string> {
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post<unknown, { demoURL?: string }>("/generateBookDemo", fd, {
        params: {id},
    });
    return res?.demoURL ?? "";
}

/** field ∈ demoURL | coverFullSize | licenseURL | audiobook.cover | audiobook.demoURL */
export async function uploadBookAsset(
    id: string,
    field: string,
    file: File,
): Promise<string> {
    const fd = new FormData();
    fd.append("file", file);
    const res = await api.post<unknown, { url: string }>("/uploadBookAsset", fd, {
        params: {id, field},
    });
    return res.url;
}

export interface AudiobookUploadParams {
    id: string; // NEW: потрібен gateway'у для запису audiobook.lcpId у Firestore
    title: string;
    files: File[];
    coverUrl?: string; // was: cover?: File — server fetches the cover itself, no CORS issue
    author?: string;
    narrator?: string;
    publisher?: string;
    language?: string;
    chapters: string[];
}

export async function uploadAudiobook(
    params: AudiobookUploadParams,
): Promise<string> {
    const fd = new FormData();
    params.files.forEach((f) => fd.append("files[]", f));
    if (params.coverUrl) fd.append("coverUrl", params.coverUrl);
    if (params.author) fd.append("author", params.author);
    if (params.narrator) fd.append("narrator", params.narrator);
    if (params.publisher) fd.append("publisher", params.publisher);
    fd.append("language", params.language || "uk");
    params.chapters.forEach((c, i) => fd.append(`chapters[${i}]`, c));

    const res = await api.post<unknown, string>("/audiobookUpload", fd, {
        params: {id: params.id, title: params.title},
    });
    return res;
}

export async function createBook(payload: CreateBookPayload): Promise<string[]> {
    const res = await api.post<unknown, CreateBookResponse>("/createBook", payload);
    return res.ids;
}

export interface UpdateBookPayload {
    type: "ebook" | "audiobook";
    name: string;
    authors: string[];
    genres: string[];
    publishingHouse: string;
    description?: string;
    descriptionHTML?: string;
    licenseURL?: string | null;
    cover?: string;
    coverFullSize?: string;
    demoURL?: string | null;
    ISBN?: string;
    recommendedPrice?: string | null;
    publishingDate?: string;
    rightsValidityTerm?: string;
    isPublic: boolean;
    showDemo: boolean;
    lcpId?: string | null;
    chapters?: string[]; // тільки для audiobook
}

export interface UpdateBookResponse {
    id: string; // може відрізнятись від переданого id — бекенд міг створити сиблінга
}

/** Оновлює документ формату `payload.type`. Якщо такого формату ще нема в
 * `relations` анкорного `id` — бекенд створить новий документ і поверне його id. */
export async function updateBook(
    id: string,
    payload: UpdateBookPayload,
): Promise<UpdateBookResponse> {
    return api.put<unknown, UpdateBookResponse>("/updateBook", payload, {
        params: {id},
    });
}